import {
    Dispatch,
    SetStateAction,
    createContext,
    useContext,
    useEffect,
    useState,
} from 'react'
import { toast } from 'react-toastify'
import { axiosInstance } from '../../helpers'
import {
    ConfirmGeneratedPayload,
    CreateTaskData,
    GenerateProjectDraftInput,
    UpdateTaskData,
    VENDOR_PROJECT_AI_MAX_DESCRIPTION_LENGTH,
    VENDOR_PROJECT_AI_MAX_FILE_SIZE,
    VENDOR_PROJECT_AI_MIN_DESCRIPTION_LENGTH,
    VENDOR_PROJECT_AI_SUPPORTED_FILE_EXTENSIONS,
    VendorProject,
    VendorProjectDocument,
    VendorProjectFormData,
    VendorTask,
    VendorTaskStatus,
    mutateProjectTasks,
    recomputeTaskCounts,
    removeProject,
    upsertProject,
} from '../../models'
import { useUser } from '../../hooks'

export interface IVendorProjectContext {
    allVendorProjects: VendorProject[]
    setAllVendorProjects: Dispatch<SetStateAction<VendorProject[]>>
    isLoading: boolean
    setIsLoading: Dispatch<SetStateAction<boolean>>
    error: string | null
    setError: Dispatch<SetStateAction<string | null>>
}

export const VendorProjectContext = createContext<IVendorProjectContext>(
    (null as unknown) as IVendorProjectContext,
)

const PROJECTS_BASE = 'vendor-project/projects/'
const TASKS_BASE = 'vendor-project/tasks/'
const GENERATE_PROJECT_DRAFT = `${PROJECTS_BASE}generate_project_draft/`
const CONFIRM_GENERATED_PROJECT = `${PROJECTS_BASE}confirm_generated/`

/** Defensive list-shape parser per §3.1 / §10. */
const extractProjectsArray = (data: unknown): VendorProject[] => {
    if (Array.isArray(data)) return data as VendorProject[]
    if (data && typeof data === 'object') {
        const obj = data as { results?: unknown; projects?: unknown }
        if (Array.isArray(obj.results)) return obj.results as VendorProject[]
        if (Array.isArray(obj.projects)) return obj.projects as VendorProject[]
    }
    return []
}

/** Backend returns `project` either as a number or `{ id }`. */
const extractParentProjectId = (task: VendorTask | unknown): number | null => {
    if (!task || typeof task !== 'object') return null
    const raw = (task as { project?: unknown }).project
    if (typeof raw === 'number') return raw
    if (raw && typeof raw === 'object' && 'id' in (raw as object)) {
        const id = (raw as { id?: unknown }).id
        return typeof id === 'number' ? id : null
    }
    return null
}

export const vendorProjectApi = () => {
    const state = useContext(VendorProjectContext)
    const { workspaceUser } = useUser()

    const fetchVendorProjects = async (): Promise<void> => {
        state.setIsLoading(true)
        state.setError(null)
        try {
            const res = await axiosInstance.get(PROJECTS_BASE, {
                params: { include_tasks: true },
            })
            const projects = extractProjectsArray(res.data).map(
                recomputeTaskCounts,
            )
            state.setAllVendorProjects(projects)
        } catch (e) {
            const msg = `Failed to load projects: ${e}`
            state.setError(msg)
            toast.error(msg)
        } finally {
            state.setIsLoading(false)
        }
    }

    const refreshProjects = fetchVendorProjects

    /**
     * Pull a human-readable message out of an axios error. DRF tends to
     * respond with an object of field arrays (e.g. `{ end_date: ["..."] }`)
     * or a single `detail` string; we flatten that down so the dialog can
     * surface it inline.
     */
    const extractErrorMessage = (e: unknown, fallback: string): string => {
        const err = e as {
            response?: { data?: unknown }
            message?: string
        }
        const data = err?.response?.data
        if (typeof data === 'string') return data
        if (data && typeof data === 'object') {
            const obj = data as Record<string, unknown>
            if (typeof obj.detail === 'string') return obj.detail
            const parts: string[] = []
            for (const key of Object.keys(obj)) {
                const v = obj[key]
                if (Array.isArray(v)) {
                    parts.push(`${key}: ${v.join(', ')}`)
                } else if (typeof v === 'string') {
                    parts.push(`${key}: ${v}`)
                }
            }
            if (parts.length > 0) return parts.join(' ��� ')
        }
        if (err?.message) return err.message
        return fallback
    }

    const createVendorProject = async (
        data: VendorProjectFormData,
    ): Promise<VendorProject | null> => {
        if (!workspaceUser) {
            const msg = 'Cannot create a project before sign-in finishes.'
            toast.error(msg)
            throw new Error(msg)
        }
        try {
            const body = {
                title: data.title,
                description: data.description ?? '',
                assigned_by_id: workspaceUser.id,
                start_date: data.start_date,
                end_date: data.end_date,
                priority: data.priority,
                address: data.address ?? '',
            }
            const res = await axiosInstance.post(PROJECTS_BASE, body)
            const created: VendorProject = recomputeTaskCounts({
                ...(res.data as VendorProject),
                tasks: [],
            })
            state.setAllVendorProjects((prev) => upsertProject(prev, created))
            return created
        } catch (e) {
            const msg = extractErrorMessage(e, 'Failed to create project')
            toast.error(msg)
            throw new Error(msg)
        }
    }

    const updateVendorProject = async (
        id: number,
        data: Partial<VendorProject>,
    ): Promise<void> => {
        try {
            const res = await axiosInstance.patch(
                `${PROJECTS_BASE}${id}/`,
                data,
            )
            const updated = res.data as VendorProject
            state.setAllVendorProjects((prev) =>
                prev.map((p) =>
                    p.id === id
                        ? recomputeTaskCounts({
                              ...p,
                              ...updated,
                              tasks: updated.tasks ?? p.tasks,
                          })
                        : p,
                ),
            )
        } catch (e) {
            const msg = extractErrorMessage(e, 'Failed to update project')
            toast.error(msg)
            throw new Error(msg)
        }
    }

    const generateProjectDraft = async (
        input: GenerateProjectDraftInput,
    ): Promise<VendorProjectDocument> => {
        const file = input.file ?? null
        const description = input.description?.trim() ?? ''

        if (!file && !description) {
            throw new Error('Either a file or description is required.')
        }

        try {
            if (
                description &&
                description.length < VENDOR_PROJECT_AI_MIN_DESCRIPTION_LENGTH
            ) {
                throw new Error(
                    `Description must be at least ${VENDOR_PROJECT_AI_MIN_DESCRIPTION_LENGTH} characters.`,
                )
            }
            if (description.length > VENDOR_PROJECT_AI_MAX_DESCRIPTION_LENGTH) {
                throw new Error(
                    `Description must be ${VENDOR_PROJECT_AI_MAX_DESCRIPTION_LENGTH} characters or fewer.`,
                )
            }

            if (file) {
                const lowerName = file.name.toLowerCase()
                const supported = VENDOR_PROJECT_AI_SUPPORTED_FILE_EXTENSIONS.some(
                    (ext) => lowerName.endsWith(ext),
                )
                if (!supported) {
                    throw new Error(
                        `Unsupported file type. Allowed file types: ${VENDOR_PROJECT_AI_SUPPORTED_FILE_EXTENSIONS.join(
                            ', ',
                        )}`,
                    )
                }
                if (file.size > VENDOR_PROJECT_AI_MAX_FILE_SIZE) {
                    throw new Error('File must be 10MB or smaller.')
                }

                const formData = new FormData()
                formData.append('file', file)
                if (description) {
                    formData.append('description', description)
                }
                const res = await axiosInstance.post(
                    GENERATE_PROJECT_DRAFT,
                    formData,
                )
                return res.data as VendorProjectDocument
            }

            const res = await axiosInstance.post(GENERATE_PROJECT_DRAFT, {
                description,
            })
            return res.data as VendorProjectDocument
        } catch (e) {
            const msg = extractErrorMessage(
                e,
                'Failed to generate project draft',
            )
            toast.error(msg)
            throw new Error(msg)
        }
    }

    const confirmGeneratedProject = async (
        payload: ConfirmGeneratedPayload,
    ): Promise<VendorProject> => {
        try {
            const res = await axiosInstance.post(
                CONFIRM_GENERATED_PROJECT,
                payload,
            )
            const created = recomputeTaskCounts(res.data as VendorProject)
            state.setAllVendorProjects((prev) => upsertProject(prev, created))
            return created
        } catch (e) {
            const msg = extractErrorMessage(
                e,
                'Failed to confirm generated project',
            )
            toast.error(msg)
            throw new Error(msg)
        }
    }

    const deleteVendorProject = async (id: number): Promise<void> => {
        try {
            await axiosInstance.delete(`${PROJECTS_BASE}${id}/`)
            state.setAllVendorProjects((prev) => removeProject(prev, id))
        } catch (e) {
            toast.error(`Failed to delete project: ${e}`)
        }
    }

    const createProjectTask = async (
        data: CreateTaskData,
        projectId: number,
    ): Promise<VendorTask | null> => {
        if (!workspaceUser) {
            const msg = 'Cannot create a task before sign-in finishes.'
            toast.error(msg)
            throw new Error(msg)
        }
        try {
            const body = {
                ...data,
                assigned_by_id: workspaceUser.id,
                project: projectId,
            }
            const res = await axiosInstance.post(TASKS_BASE, body)
            const created = res.data as VendorTask
            state.setAllVendorProjects((prev) =>
                mutateProjectTasks(prev, projectId, (tasks) => [
                    ...tasks,
                    created,
                ]),
            )
            return created
        } catch (e) {
            const msg = extractErrorMessage(e, 'Failed to create task')
            toast.error(msg)
            throw new Error(msg)
        }
    }

    const updateProjectTask = async (
        taskId: number,
        data: UpdateTaskData,
    ): Promise<void> => {
        try {
            const res = await axiosInstance.patch(
                `${TASKS_BASE}${taskId}/`,
                data,
            )
            const updated = res.data as VendorTask
            const projectId = extractParentProjectId(updated)
            state.setAllVendorProjects((prev) =>
                prev.map((p) => {
                    if (projectId !== null && p.id !== projectId) {
                        // Still patch in case stale parent
                        const idx = (p.tasks ?? []).findIndex(
                            (t) => t.id === taskId,
                        )
                        if (idx === -1) return p
                    }
                    const tasks = p.tasks ?? []
                    let touched = false
                    const nextTasks = tasks.map((t) => {
                        if (t.id === taskId) {
                            touched = true
                            return { ...t, ...updated }
                        }
                        return t
                    })
                    if (!touched) return p
                    return recomputeTaskCounts({ ...p, tasks: nextTasks })
                }),
            )
        } catch (e) {
            const msg = extractErrorMessage(e, 'Failed to update task')
            toast.error(msg)
            throw new Error(msg)
        }
    }

    const deleteProjectTask = async (taskId: number): Promise<void> => {
        try {
            // Per §10, fetch the task first to discover its parent project id.
            const lookup = await axiosInstance.get(`${TASKS_BASE}${taskId}/`)
            const projectId = extractParentProjectId(lookup.data)
            await axiosInstance.delete(`${TASKS_BASE}${taskId}/`)

            state.setAllVendorProjects((prev) => {
                if (projectId !== null) {
                    return mutateProjectTasks(prev, projectId, (tasks) =>
                        tasks.filter((t) => t.id !== taskId),
                    )
                }
                // Fallback: remove from whichever project happens to hold it.
                return prev.map((p) => {
                    const tasks = p.tasks ?? []
                    if (!tasks.find((t) => t.id === taskId)) return p
                    return recomputeTaskCounts({
                        ...p,
                        tasks: tasks.filter((t) => t.id !== taskId),
                    })
                })
            })
        } catch (e) {
            toast.error(`Failed to delete task: ${e}`)
        }
    }

    const transitionProjectTask = async (
        taskId: number,
        toStatus: VendorTaskStatus,
    ): Promise<void> => {
        try {
            const res = await axiosInstance.post(
                `${TASKS_BASE}${taskId}/transition/`,
                { status: toStatus },
            )
            const updated = res.data as VendorTask
            state.setAllVendorProjects((prev) =>
                prev.map((p) => {
                    const tasks = p.tasks ?? []
                    let touched = false
                    const nextTasks = tasks.map((t) => {
                        if (t.id === taskId) {
                            touched = true
                            return { ...t, ...updated }
                        }
                        return t
                    })
                    if (!touched) return p
                    return recomputeTaskCounts({ ...p, tasks: nextTasks })
                }),
            )
        } catch (e) {
            toast.error(`Failed to update task status: ${e}`)
        }
    }

    const clearError = () => state.setError(null)

    return {
        // Data
        allVendorProjects: state.allVendorProjects,
        isLoading: state.isLoading,
        error: state.error,
        // Project ops
        fetchVendorProjects,
        refreshProjects,
        createVendorProject,
        updateVendorProject,
        generateProjectDraft,
        confirmGeneratedProject,
        deleteVendorProject,
        // Task ops
        createProjectTask,
        updateProjectTask,
        deleteProjectTask,
        transitionProjectTask,
        // Utilities
        clearError,
    }
}

export const useVendorProjectState = (): IVendorProjectContext => {
    const [allVendorProjects, setAllVendorProjects] = useState<VendorProject[]>(
        [],
    )
    const [isLoading, setIsLoading] = useState(false)
    const [error, setError] = useState<string | null>(null)

    const { workspaceUser } = useUser()

    // Re-fetch whenever the workspace user changes (per §4.2.4 / §4.2.5).
    useEffect(() => {
        if (!workspaceUser?.id) return
        let cancelled = false
        setIsLoading(true)
        setError(null)
        axiosInstance
            .get(PROJECTS_BASE, { params: { include_tasks: true } })
            .then((res) => {
                if (cancelled) return
                const projects = extractProjectsArray(res.data).map(
                    recomputeTaskCounts,
                )
                setAllVendorProjects(projects)
            })
            .catch((e) => {
                if (cancelled) return
                const msg = `Failed to load projects: ${e}`
                setError(msg)
            })
            .finally(() => {
                if (cancelled) return
                setIsLoading(false)
            })
        return () => {
            cancelled = true
        }
    }, [workspaceUser?.id])

    return {
        allVendorProjects,
        setAllVendorProjects,
        isLoading,
        setIsLoading,
        error,
        setError,
    }
}
